import React from "react";
import { Shell } from "@/components/shell";
import { Heading } from "@/components/ui/heading";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Utensils, MapPin, Clock, Star, Search, Phone } from "lucide-react";

export default function AboutPage() {
  return (
    <Shell>
      <div className="container px-4 py-8 mx-auto max-w-5xl">
        <Heading 
          title="Über SpeiseGuru" 
          description="Entdecken Sie die besten Restaurants in Ihrer Nähe"
        />
        
        <div className="mt-8 space-y-6">
          <section className="prose max-w-none dark:prose-invert text-muted-foreground">
            <p className="text-lg">
              SpeiseGuru ist die führende Restaurant-Suchplattform, die Ihnen hilft, genau das richtige Restaurant 
              für jeden Anlass zu finden. Von gehobener Küche bis zum schnellen Imbiss, von der Speisekarte 
              bis zu Öffnungszeiten - wir bieten alle Informationen, die Sie für Ihre Entscheidung benötigen.
            </p>
            
            <p>
              Unser Ziel ist es, lokale Gastronomie zu unterstützen und Ihnen gleichzeitig eine 
              bequeme Möglichkeit zu bieten, Ihre kulinarischen Optionen zu erkunden. Mit SpeiseGuru 
              sind Sie immer nur wenige Klicks von Ihrem nächsten großartigen Restauranterlebnis entfernt.
            </p>
          </section>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Search className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Schnelle Suche</CardTitle>
                <CardDescription>Finden Sie genau das, wonach Sie suchen</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-muted-foreground">
                  Dank unserer intelligenten Suchfunktion und fortschrittlichen Filter 
                  können Sie ganz einfach Restaurants nach Kategorien, Küche, Preisklasse und mehr durchsuchen.
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Standortbasiert</CardTitle>
                <CardDescription>Entdecken Sie Restaurants in Ihrer Nähe</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-muted-foreground">
                  Mit unserer Standorterkennung finden Sie sofort Restaurants in Ihrer Umgebung 
                  und sehen auf einen Blick, wie weit sie entfernt sind.
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Aktuelle Öffnungszeiten</CardTitle>
                <CardDescription>Sehen Sie auf einen Blick, was geöffnet ist</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-muted-foreground">
                  Wir zeigen Ihnen in Echtzeit, welche Restaurants geöffnet haben und 
                  wann Sie vorbeischauen oder anrufen können.
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Utensils className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Speisekarten-Galerie</CardTitle>
                <CardDescription>Speisekarten durchstöbern, bevor Sie sich entscheiden</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-muted-foreground">
                  Durchstöbern Sie hochwertige Bilder von Speisekarten und treffen Sie 
                  Ihre Entscheidung bereits vor dem Besuch.
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Star className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Bewertungen & Favoriten</CardTitle>
                <CardDescription>Entdecken Sie Empfehlungen anderer Nutzer</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-muted-foreground">
                  Lesen Sie authentische Bewertungen, speichern Sie Ihre Lieblingsrestaurants 
                  und teilen Sie Ihre eigenen Erfahrungen mit der Community.
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Direkter Kontakt</CardTitle>
                <CardDescription>Verbinden Sie sich mit Restaurants</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-muted-foreground">
                  Mit einem Klick haben Sie Zugriff auf alle wichtigen Kontaktdaten, 
                  um direkt Kontakt aufzunehmen.
                </p>
              </CardContent>
            </Card>
          </div>

          <section className="mt-16 text-center">
            <h2 className="text-2xl font-bold mb-4">Entdecken Sie die kulinarische Vielfalt</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              SpeiseGuru ist Ihr vertrauenswürdiger Begleiter für kulinarische Entdeckungen. 
              Egal ob Sie eine schnelle Mahlzeit suchen oder einen besonderen Anlass feiern - 
              wir helfen Ihnen, das perfekte Restaurant zu finden.
            </p>
            <div className="mt-8">
              <a 
                href="/" 
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-primary hover:bg-primary/90 rounded-md shadow-sm"
              >
                Jetzt Restaurants entdecken
              </a>
            </div>
          </section>
        </div>
      </div>
    </Shell>
  );
}